/**
 * useLeadCapture — shared hook for lead capture state management.
 *
 * Encapsulates sessionStorage restoration, form visibility logic,
 * and the submission callback used by all panel components.
 */
import { useState } from 'preact/hooks';
import type { LeadCaptureConfig } from './types';
import { leadStorageKey } from './storage';

interface UseLeadCaptureResult {
  showLeadForm: boolean;
  leadCaptureConfig: LeadCaptureConfig | null | undefined;
  handleLeadSubmitted: (leadId: string, conversationId?: string) => void;
}

export function useLeadCapture(
  siteId: string,
  leadCaptureConfig: LeadCaptureConfig | null | undefined,
  onLeadConversation?: (conversationId: string, isRestore?: boolean) => void,
): UseLeadCaptureResult {
  const [leadSubmitted, setLeadSubmitted] = useState<boolean>(() => {
    try {
      const raw = sessionStorage.getItem(leadStorageKey(siteId));
      if (!raw) return false;
      try {
        const stored = JSON.parse(raw) as { conversationId?: string };
        if (stored.conversationId && onLeadConversation) {
          onLeadConversation(stored.conversationId, true);
        }
      } catch {
        // Legacy format — still counts as submitted
      }
      return true;
    } catch {
      return false;
    }
  });

  const showLeadForm = leadCaptureConfig?.enabled === true && !leadSubmitted;

  const handleLeadSubmitted = (leadId: string, conversationId?: string): void => {
    void leadId;
    if (conversationId && onLeadConversation) {
      onLeadConversation(conversationId);
    }
    setLeadSubmitted(true);
  };

  return { showLeadForm, leadCaptureConfig, handleLeadSubmitted };
}
